import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiClientError } from '../api/client';
import { changePassword } from '../api/auth';
import {
  beginCalendarConnect,
  calendarStatus,
  connectServiceCalendar,
  disconnectCalendar,
  disconnectServiceCalendar,
  listInboundLog,
  listServiceCalendars,
  listSyncHealth,
  listSettings,
  resetSetting,
  setSetting,
  type CalendarProviderId,
  type InboundLogEntry,
  type Setting,
} from '../api/administration';
import { useAuth } from '../auth/AuthProvider';
import { PasswordField } from '../components/PasswordField';
import { useToast } from '../components/Toasts';
import { Icon } from '../lib/icons';
import { CONNECTION_BADGE, badgeFor } from '../lib/statusStyles';

/**
 * FEAT-022 / FEAT-023 — Administration, and the one screen with no prototype to
 * copy. §22 requires it and the prototype never had it, so the layout is built
 * from the prototype's own vocabulary — `page-head`, `card`, `note`, `field` —
 * rather than invented, which keeps it recognisably the same application.
 *
 * Two audiences on one screen, deliberately:
 *
 *   - **My calendar** is every employee's own connection. It needs no
 *     capability, because consent is theirs to give (ADR 0006) and there is no
 *     route by which anyone — Super Admin included — could give it for them.
 *   - **Platform settings** needs `Settings.Manage`. It is hidden without it as
 *     a courtesy; the server rejects the call regardless.
 */
export function SettingsPage(): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">{t('nav_admin')}</div>
          <h1>{t('settings')}</h1>
          <div className="lead">{t('settings_lead')}</div>
        </div>
      </div>

      {/*
        Above the calendar card, and first on the page: it is the one thing here
        that every single person can do, whatever their role. Everything below is
        either an integration or an administrator's setting.
      */}
      <ChangePasswordCard />

      <MyCalendarCard />

      {/*
        FEAT-018 Stage 6 — both behind `Integrations.Manage`, which is the
        capability that configures the connection they report on. The inbound log
        carries external meeting titles, and "Termination discussion — Ahmed"
        must not be readable by everyone who may book that room.
      */}
      {can('Integrations.Manage') ? <RoomCalendarReaderCard /> : null}
      {can('Integrations.Manage') ? <SyncHealthCard /> : null}
      {can('Integrations.Manage') ? <ShadowLogCard /> : null}

      {can('Settings.Manage') ? <PlatformSettingsCard /> : null}
    </>
  );
}

/**
 * Eight rows. The security log shows twenty-five and owns its screen; this card
 * shares Administration with four others, and the ask was explicitly that it not
 * take up much room.
 */
const SHADOW_PAGE_SIZE = 8;

/* ------------------------------------------------------------------ password */

/**
 * REQ-005 — changing your own password.
 *
 * The current password is asked for because the server requires it, and the
 * server requires it because a session is not proof of identity: an unlocked
 * screen is a session. Saying so in the note matters as much as enforcing it —
 * a field that looks redundant gets read as the form being badly designed.
 */
function ChangePasswordCard(): ReactElement {
  const { t } = useTranslation();
  const { notify } = useToast();
  const { signOut } = useAuth();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => changePassword(current, next),
    onSuccess: async () => {
      notify(t('pw_changed'));

      /*
       * The change revoked every session on the server, this one included. Not
       * signing out here would leave the user in a shell where every request
       * 401s — technically signed in, functionally broken, and confusing in a
       * way that reads as the password change having failed.
       */
      await signOut();
    },
    onError: (error: unknown) => {
      if (error instanceof ApiClientError) {
        const fields = error.fieldErrors ?? {};
        setFieldErrors(fields);

        /*
         * The banner is suppressed only when the error is actually *shown*
         * somewhere — not merely when the server named some field.
         *
         * It used to hide the banner whenever any field error existed, so an
         * error naming a field this form does not render vanished completely:
         * no banner, nothing under any input, and a button that appeared to do
         * nothing. That is exactly what happened when the server called the
         * field `password` and this card was watching `new_password`.
         *
         * Both halves are fixed, but this is the half that stops the next
         * mismatch being silent too.
         */
        const rendered = ['current_password', 'new_password', 'confirm'];
        const shown = Object.keys(fields).some((field) => rendered.includes(field));

        setBanner(shown ? null : (Object.values(fields)[0] ?? error.message));
        return;
      }

      setBanner(t('login_network_error'));
    },
  });

  function submit(event: FormEvent): void {
    event.preventDefault();

    setBanner(null);
    setFieldErrors({});

    /*
     * Compared in the browser only, and deliberately so. The confirm field is
     * pure UX — there is nothing for the server to check, because the two values
     * are the same field typed twice. The password *rules* are the server's, and
     * its 422 is what renders under the field.
     */
    if (next !== confirm) {
      setFieldErrors({ confirm: t('pw_mismatch') });
      return;
    }

    // Re-hide before the request, so the password is not left on screen while a
    // slow round trip finishes.
    setRevealed(false);
    mutation.mutate();
  }

  return (
    <div className="card">
      <div className="card-head">
        <h2>{t('pw_card')}</h2>
      </div>

      {/*
        Same body shell as the calendar card under it: without `.set-body` the
        fields sit flush against the card edge, and a free-floating hint sibling
        (outside each `.field`) collides with the next label.
      */}
      <div className="note">
        <Icon name="info" />
        <div>{t('pw_card_note')}</div>
      </div>

      <div className="set-body">
        {banner ? (
          <div className="field-error" role="alert">
            {banner}
          </div>
        ) : null}

        <form onSubmit={submit}>
          <PasswordField
            id="pw-current"
            label={t('pw_current')}
            value={current}
            onChange={setCurrent}
            autoComplete="current-password"
            disabled={mutation.isPending}
            revealed={revealed}
            onRevealedChange={setRevealed}
            error={fieldErrors.current_password}
          />

          <PasswordField
            id="pw-new"
            label={t('pw_new')}
            value={next}
            onChange={setNext}
            autoComplete="new-password"
            disabled={mutation.isPending}
            revealed={revealed}
            onRevealedChange={setRevealed}
            hint={t('pw_hint')}
            error={fieldErrors.new_password}
          />

          <PasswordField
            id="pw-confirm"
            label={t('pw_confirm')}
            value={confirm}
            onChange={setConfirm}
            autoComplete="new-password"
            disabled={mutation.isPending}
            revealed={revealed}
            onRevealedChange={setRevealed}
            error={fieldErrors.confirm}
          />

          <div className="row-actions">
            <button
              type="submit"
              className="btn primary"
              disabled={mutation.isPending || !current || !next || !confirm}
            >
              {t('pw_submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ calendar */

/**
 * The outcome the provider redirected back with.
 *
 * Read from the query string once and then cleared, so a refresh does not
 * re-announce a connection that happened minutes ago.
 */
function useConnectOutcome(): 'connected' | 'cancelled' | 'failed' | null {
  const [params, setParams] = useSearchParams();
  const outcome = params.get('calendar');
  const [captured] = useState(outcome);

  useEffect(() => {
    if (!outcome) return;
    const next = new URLSearchParams(params);
    next.delete('calendar');
    setParams(next, { replace: true });
  }, [outcome, params, setParams]);

  if (captured !== 'connected' && captured !== 'cancelled' && captured !== 'failed') return null;
  return captured;
}

function MyCalendarCard(): ReactElement {
  const { t } = useTranslation();
  const { notify } = useToast();
  const queryClient = useQueryClient();
  const outcome = useConnectOutcome();

  const status = useQuery({ queryKey: ['calendar', 'me'], queryFn: calendarStatus });

  useEffect(() => {
    if (outcome === 'connected') notify(t('cal_connected'));
    if (outcome === 'failed') notify(t('cal_failed'));
  }, [outcome, notify, t]);

  const connect = useMutation({
    mutationFn: beginCalendarConnect,
    onSuccess: (result) => {
      /*
       * A full navigation, not a popup or an iframe. Google and Microsoft both
       * refuse to render their consent screens in a frame — deliberately, since
       * a framed consent screen is how consent gets phished — and a popup is
       * blocked often enough to be an unreliable path to the only screen that
       * can grant access.
       */
      window.location.assign(result.url);
    },
    onError: (error: unknown) => {
      notify(error instanceof ApiClientError ? error.message : t('login_network_error'));
    },
  });

  const disconnect = useMutation({
    mutationFn: disconnectCalendar,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendar', 'me'] });
      notify(t('cal_disconnected'));
    },
  });

  const data = status.data;

  return (
    <div className="card">
      <div className="card-head">
        <h2>{t('cal_my_calendar')}</h2>
      </div>

      <div className="note">
        <Icon name="info" />
        <div>{t('cal_note')}</div>
      </div>

      {status.isPending ? (
        <div className="empty" role="status" aria-live="polite">
          {t('loading')}
        </div>
      ) : status.error || !data ? (
        <div className="empty" role="alert">
          {status.error instanceof ApiClientError ? status.error.message : t('login_network_error')}
        </div>
      ) : (
        <div className="set-body">
          {data.connected ? (
            <p className="set-state">
              <span className={badgeFor(CONNECTION_BADGE, 'connected')}>
                {t('cal_connected_as')}
              </span>{' '}
              {data.account_email}
            </p>
          ) : data.needs_reconnect ? (
            <p className="set-state">
              <span className={badgeFor(CONNECTION_BADGE, 'needs_reconnect')}>
                {t('cal_needs_reconnect')}
              </span>{' '}
              {t('cal_needs_reconnect_help')}
            </p>
          ) : (
            <p className="set-state">{t('cal_not_connected')}</p>
          )}

          <div className="set-actions">
            {data.available.map((provider) => (
              <ProviderButton
                key={provider.provider}
                label={provider.label}
                available={provider.available}
                reason={provider.reason}
                isCurrent={data.connected && data.provider === provider.provider}
                pending={connect.isPending}
                onConnect={() => connect.mutate(provider.provider)}
              />
            ))}

            {data.connected || data.needs_reconnect ? (
              <button
                type="button"
                className="btn danger"
                onClick={() => disconnect.mutate()}
                disabled={disconnect.isPending}
              >
                {t('cal_disconnect')}
              </button>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

interface ProviderButtonProps {
  label: string;
  available: boolean;
  reason?: string;
  isCurrent: boolean;
  pending: boolean;
  onConnect: () => void;
}

function ProviderButton({
  label,
  available,
  reason,
  isCurrent,
  pending,
  onConnect,
}: ProviderButtonProps): ReactElement {
  const { t } = useTranslation();

  return (
    <span className="set-provider">
      <button
        type="button"
        className={isCurrent ? 'btn' : 'btn primary'}
        onClick={onConnect}
        disabled={!available || pending}
      >
        <Icon name="calendar" />
        {isCurrent
          ? t('cal_reconnect', { provider: label })
          : t('cal_connect', { provider: label })}
      </button>
      {/*
        The reason a provider cannot be used, rather than a disabled button with
        no explanation. Without it the only way to find out is to ask an
        administrator whether they have configured it.
      */}
      {available ? null : <span className="hint">{reason ?? t('cal_unconfigured')}</span>}
    </span>
  );
}

/* ------------------------------------------------------------------ settings */

/** The prefix before the first dot, which is how the FRS groups the catalogue. */
function groupOf(key: string): string {
  return key.split('.')[0] ?? key;
}

function PlatformSettingsCard(): ReactElement {
  const { t } = useTranslation();
  const settings = useQuery({ queryKey: ['settings'], queryFn: listSettings });

  const groups = new Map<string, Setting[]>();
  for (const item of settings.data ?? []) {
    const group = groupOf(item.key);
    groups.set(group, [...(groups.get(group) ?? []), item]);
  }

  return (
    <div className="card">
      <div className="card-head">
        <h2>{t('settings_platform')}</h2>
      </div>

      <div className="note">
        <Icon name="shield" />
        <div>{t('settings_secret_note')}</div>
      </div>

      {settings.isPending ? (
        <div className="empty" role="status" aria-live="polite">
          {t('loading')}
        </div>
      ) : settings.error ? (
        <div className="empty" role="alert">
          {settings.error instanceof ApiClientError
            ? settings.error.message
            : t('login_network_error')}
        </div>
      ) : (
        [...groups.entries()].map(([group, items]) => (
          <section key={group} className="set-group">
            <h3>{group}</h3>
            {items.map((item) => (
              <SettingRow key={item.key} setting={item} />
            ))}
          </section>
        ))
      )}
    </div>
  );
}

function SettingRow({ setting }: { setting: Setting }): ReactElement {
  const { t } = useTranslation();
  const { notify } = useToast();
  const queryClient = useQueryClient();

  const [draft, setDraft] = useState('');
  const fieldId = `set-${setting.key.replace(/\./g, '-')}`;

  const save = useMutation({
    mutationFn: (value: string) => setSetting(setting.key, value),
    onSuccess: (rows) => {
      queryClient.setQueryData(['settings'], rows);
      setDraft('');
      notify(t('settings_saved'));
    },
    onError: (error: unknown) => {
      notify(error instanceof ApiClientError ? error.message : t('login_network_error'));
    },
  });

  const reset = useMutation({
    mutationFn: () => resetSetting(setting.key),
    onSuccess: (rows) => {
      queryClient.setQueryData(['settings'], rows);
      notify(t('settings_reset_done'));
    },
  });

  /*
   * A sensitive field starts empty every time, and empty means "leave it
   * alone". Pre-filling it with a placeholder that is then submitted verbatim
   * is how a credential store gets overwritten with bullet characters.
   */
  const current = setting.sensitive ? '' : String(setting.value ?? '');

  return (
    <div className="set-row">
      <div className="set-label">
        <label htmlFor={fieldId}>{setting.key}</label>
        <span className="hint">{setting.description}</span>
      </div>

      <div className="set-control">
        {setting.type === 'boolean' ? (
          <select
            id={fieldId}
            value={draft || current}
            onChange={(event) => save.mutate(event.target.value)}
            disabled={save.isPending}
          >
            <option value="true">{t('yes')}</option>
            <option value="false">{t('no')}</option>
          </select>
        ) : (
          <>
            <input
              id={fieldId}
              type={setting.sensitive ? 'password' : 'text'}
              value={draft || (setting.sensitive ? '' : current)}
              placeholder={setting.sensitive ? t('settings_secret_placeholder') : ''}
              autoComplete={setting.sensitive ? 'new-password' : 'off'}
              onChange={(event) => setDraft(event.target.value)}
            />
            <button
              type="button"
              className="btn sm primary"
              onClick={() => save.mutate(draft)}
              disabled={draft.length === 0 || save.isPending}
            >
              {t('save')}
            </button>
          </>
        )}

        {setting.is_default ? null : (
          <button
            type="button"
            className="btn sm"
            onClick={() => reset.mutate()}
            disabled={reset.isPending}
          >
            {t('settings_reset')}
          </button>
        )}
      </div>

      <div className="set-status">
        {setting.sensitive ? (
          <span className={badgeFor(CONNECTION_BADGE, setting.is_set ? 'is_set' : 'not_set')}>
            {setting.is_set ? t('settings_is_set') : t('settings_not_set')}
          </span>
        ) : setting.is_default ? (
          <span className={badgeFor(CONNECTION_BADGE, 'default')}>{t('settings_default')}</span>
        ) : (
          <span className={badgeFor(CONNECTION_BADGE, 'overridden')}>
            {t('settings_overridden')}
          </span>
        )}
      </div>
    </div>
  );
}

/* --------------------------------------------------- the room-calendar reader */

/**
 * FEAT-018 Stage 6 / ADR 0009 — the account Boud reads room calendars with.
 *
 * Deliberately a *separate* card from "My calendar" above, though they look
 * alike. One is a person's own consent, which nobody else can give for them; the
 * other is a platform-wide connection that decides what the inbound worker can
 * see at all. Folding them together would invite somebody to connect their
 * personal account here, which is exactly what ADR 0009 argues against.
 */
function RoomCalendarReaderCard(): ReactElement {
  const { t } = useTranslation();
  const { notify } = useToast();
  const queryClient = useQueryClient();

  const readers = useQuery({ queryKey: ['calendar', 'service'], queryFn: listServiceCalendars });

  const connect = useMutation({
    mutationFn: (provider: CalendarProviderId) => connectServiceCalendar(provider),
    // A full navigation, not a fetch — the provider's consent screen is a page.
    onSuccess: ({ url }) => window.location.assign(url),
    onError: (error) =>
      notify(error instanceof ApiClientError ? error.message : t('login_network_error'), 'bad'),
  });

  const disconnect = useMutation({
    mutationFn: (provider: CalendarProviderId) => disconnectServiceCalendar(provider),
    onSuccess: () => {
      notify(t('cal_reader_disconnected'));
      void queryClient.invalidateQueries({ queryKey: ['calendar', 'service'] });
    },
  });

  const connectable = (['google', 'microsoft'] as CalendarProviderId[]).filter(
    (provider) => !readers.data?.some((r) => r.provider === provider),
  );

  return (
    <div className="card">
      <div className="card-head">
        <h2>{t('cal_reader')}</h2>
      </div>

      <div className="note">
        <Icon name="info" />
        <div>{t('cal_reader_note')}</div>
      </div>

      {/*
        Empty state uses `.set-state`, not `.empty`: the latter is a centred
        table-empty treatment, and next to the left-aligned calendar card above
        it read as a different layout rule for the same kind of fact.
      */}
      {readers.data?.length === 0 ? (
        <div className="set-body">
          <p className="set-state">{t('cal_reader_none')}</p>
          {connectable.length > 0 ? (
            <div className="set-actions">
              {connectable.map((provider) => (
                <button
                  key={provider}
                  type="button"
                  className="btn"
                  disabled={connect.isPending}
                  onClick={() => connect.mutate(provider)}
                >
                  {t('cal_reader_connect', { provider: t(`cal_${provider}`) })}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">{t('cal_provider')}</th>
                  <th scope="col">{t('cal_account')}</th>
                  <th scope="col">
                    <span className="sr-only">{t('actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {readers.data?.map((reader) => (
                  <tr key={reader.provider}>
                    <td>{t(`cal_${reader.provider}`)}</td>
                    <td>
                      {/* Latin address in an Arabic page — see the `<bdi>` trap. */}
                      <bdi>{reader.account_email}</bdi>
                      {reader.needs_attention ? (
                        <div className="field-error" role="alert">
                          {t('cal_reader_lapsed')}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn sm danger"
                        onClick={() => disconnect.mutate(reader.provider)}
                      >
                        {t('cal_disconnect')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {connectable.length > 0 ? (
            <div className="set-body">
              <div className="set-actions">
                {connectable.map((provider) => (
                  <button
                    key={provider}
                    type="button"
                    className="btn"
                    disabled={connect.isPending}
                    onClick={() => connect.mutate(provider)}
                  >
                    {t('cal_reader_connect', { provider: t(`cal_${provider}`) })}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

/* -------------------------------------------------------------- sync health */

/**
 * FEAT-018 Stage 9 — when was each room last actually read?
 *
 * Every way inbound sync can stop degrades gracefully: a lapsed credential, an
 * expired cursor, a provider outage. None of them breaks anything visibly —
 * bookings made elsewhere simply stop arriving, and the first sign is somebody
 * walking into an occupied room weeks later.
 *
 * Which is why the column that matters is **how long since** a room was read,
 * not whether the last attempt happened to work.
 */
function SyncHealthCard(): ReactElement {
  const { t } = useTranslation();

  const health = useQuery({
    queryKey: ['calendar', 'health'],
    queryFn: listSyncHealth,
    refetchInterval: 60_000,
  });

  const stale = (health.data ?? []).filter((row) => row.stale);

  return (
    <div className="card">
      <div className="card-head">
        <h2>{t('cal_health')}</h2>
        {stale.length > 0 ? <span className="badge bad">{stale.length}</span> : null}
      </div>

      {health.data?.length === 0 ? (
        <div className="empty">{t('cal_health_none')}</div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">{t('rm_name')}</th>
                <th scope="col">{t('cal_health_last')}</th>
                <th scope="col">{t('cal_health_inbound')}</th>
                <th scope="col">{t('cal_health_note')}</th>
              </tr>
            </thead>
            <tbody>
              {health.data?.map((row) => (
                <tr key={row.room_id}>
                  <td>{row.room_name}</td>
                  <td>
                    <span className={row.stale ? 'badge bad' : 'badge ok'}>
                      {row.minutes_since_sweep === null
                        ? t('cal_health_never')
                        : t('cal_health_ago', { minutes: row.minutes_since_sweep })}
                    </span>
                  </td>
                  <td>{row.inbound_enabled ? t('cal_health_on') : t('cal_health_off')}</td>
                  <td>
                    {/* The provider's own words — diagnostic text, left as it came. */}
                    <bdi>{row.last_error}</bdi>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------- shadow mode */

/** How each decision is coloured. Only `conflict` is alarming. */
const DECISION_BADGE: Record<InboundLogEntry['decision'], string> = {
  own_echo: 'badge',
  would_create: 'badge ok',
  would_update: 'badge ok',
  would_cancel: 'badge warn',
  conflict: 'badge bad',
  ignored: 'badge',
};

/**
 * FEAT-018 Stage 6 — what the inbound worker *would* have done.
 *
 * This screen is the deliverable of the whole stage. Nothing is applied yet: the
 * worker reads each room's calendar, decides, and records — and a week of these
 * rows read by a person is what proves the classification is right before Stage
 * 7 lets it touch a booking. A log nobody can read would make that week
 * impossible, which is why this is not deferred to "later".
 */
function ShadowLogCard(): ReactElement {
  const { t } = useTranslation();
  const [decision, setDecision] = useState('');
  const [page, setPage] = useState(1);

  const log = useQuery({
    queryKey: ['calendar', 'inbound-log', decision, page],
    queryFn: () =>
      listInboundLog({
        page,
        pageSize: SHADOW_PAGE_SIZE,
        ...(decision ? { decision } : {}),
      }),
    // The previous page stays on screen while the next loads, so paging does not
    // blank the table and throw the scroll position back to the top.
    placeholderData: (previous) => previous,
    // A live view of a background worker. Stale data here is the one thing that
    // would make an administrator draw the wrong conclusion.
    refetchInterval: 30_000,
  });

  return (
    <div className="card">
      <div className="card-head">
        <h2>{t('cal_shadow')}</h2>
        <span className="hint">{log.data?.meta.total_items ?? 0}</span>
      </div>

      <div className="note">
        <Icon name="info" />
        <div>{t('cal_shadow_note')}</div>
      </div>

      <div className="field">
        <label htmlFor="shadow-filter">{t('cal_shadow_filter')}</label>
        <select
          id="shadow-filter"
          value={decision}
          className="fb-input"
          onChange={(event) => {
            setDecision(event.target.value);
            // Page 4 of a different filter is meaningless.
            setPage(1);
          }}
        >
          <option value="">{t('cal_shadow_all')}</option>
          {Object.keys(DECISION_BADGE).map((value) => (
            <option key={value} value={value}>
              {t(`cal_decision_${value}`)}
            </option>
          ))}
        </select>
      </div>

      {log.data?.items.length === 0 ? (
        <div className="empty">{t('cal_shadow_none')}</div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">{t('cal_shadow_when')}</th>
                <th scope="col">{t('rm_name')}</th>
                <th scope="col">{t('cal_shadow_decision')}</th>
                <th scope="col">{t('cal_shadow_why')}</th>
              </tr>
            </thead>
            <tbody>
              {log.data?.items.map((entry) => (
                <tr key={entry.id}>
                  <td>
                    <bdi>{new Date(entry.observed_at).toLocaleString()}</bdi>
                  </td>
                  <td>{entry.room_name}</td>
                  <td>
                    <span className={DECISION_BADGE[entry.decision]}>
                      {t(`cal_decision_${entry.decision}`)}
                    </span>
                  </td>
                  <td>
                    <div>{entry.reason}</div>
                    {entry.title ? (
                      <div className="hint">
                        <bdi>{entry.title}</bdi>
                        {entry.organiser_email ? (
                          <>
                            {' — '}
                            <bdi>{entry.organiser_email}</bdi>
                          </>
                        ) : null}
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/*
            Kept deliberately compact. The security log owns a whole screen and
            can afford twenty-five rows; this card shares Administration with
            four others, so it shows eight and pages.
          */}
          {log.data && log.data.meta.total_pages > 1 ? (
            <nav className="pager sm" aria-label={t('pg_label')}>
              <button
                type="button"
                className="btn sm"
                onClick={() => setPage((p) => p - 1)}
                disabled={page <= 1 || log.isFetching}
              >
                {t('pg_prev')}
              </button>

              <span className="pg-pos" aria-live="polite">
                {t('pg_position', { page, total: log.data.meta.total_pages })}
              </span>

              <button
                type="button"
                className="btn sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= log.data.meta.total_pages || log.isFetching}
              >
                {t('pg_next')}
              </button>
            </nav>
          ) : null}
        </div>
      )}
    </div>
  );
}
